'use strict'

const ActivityMapping = require('./activity-mapping.json')
const DbHelpers = require('./db-helpers')
const extend = require('extend')
const Logger = require('@gsm/logger')
const Policy = require('./policy')
const Q = require('q')
const Util = require('../common/util')

/**
 * Is this a valid feature policy document?
 * @param document
 * @returns {boolean}
 */
const isValid = (document) => {
    return 'object' === typeof document
}

class FeaturePolicy {

    constructor(document) {
        this._evaluatedPolicy = null
        this._needsEvaluation = true

        // this is an array of arrays
        // each element in the array is a layer
        // that is an array of policies
        this._policies = []
        if (undefined !== document) {
            this.mergeDocument(document)
        }
    }

    _evaluate() {
        if (!this._needsEvaluation) {
            return Q(this._evaluatedPolicy)
        }
        const policies = this._policies.slice(0)
        const mergeLayer = (layer) => {
            return layer.reduce((currentPolicy, nextPolicy) => {
                return currentPolicy.mergeWith(nextPolicy)
            }, layer.pop())
        }
        const initialPolicy = policies.pop() || [ Policy.create() ]
        this._evaluatedPolicy = policies.reduce((accumulatedPolicy, nextPolicyLayer) => {
            const mergedLayer = mergeLayer(nextPolicyLayer)
            return accumulatedPolicy.filterThrough(mergedLayer)
        }, mergeLayer(initialPolicy))
        this._needsEvaluation = false
        return Q(this._evaluatedPolicy)
    }

    appendDocument(document) {
        this._needsEvaluation = true
        const policy = Policy.create(document)
        this._policies.push([policy])
        return this
    }

    appendPolicy(featurePolicy) {
        return featurePolicy.asDocument()
            .then(document => this.appendDocument(document))
    }

    mergeDocument(document) {
        this._needsEvaluation = true
        const policySet = this._policies[this._policies.length - 1]
        if (!Array.isArray(policySet)) {
            return this.appendDocument(document)
        }
        const policy = Policy.create(document)
        policySet.push(policy)
        return this
    }

    mergePolicy(featurePolicy) {
        return featurePolicy.asDocument()
            .then(document => this.mergeDocument(document))
    }

    evaluateAccessPolicy(accessPolicy) {
        return this._evaluate()
            .then(featurePolicy => {
                return featurePolicy.applyTo(accessPolicy)
            })
    }

    toAccessStatements() {
        return this.asDocument()
            .then(document => {
                return document.Statement.map(s => {
                    const accessStatement = extend(true, {}, s)
                    const actions = accessStatement.Action
                    accessStatement.Action = actions.reduce((a, featureAction) => {
                        const action = Util.parseAction(featureAction)
                        const service = ActivityMapping[action.service]
                        if (!service) {
                            Logger.warn(`No access activity mapping available for feature service '${action.service}'`)
                            return a
                        }

                        let activity = [featureAction]
                        if (action.activity !== '*') {
                            activity = service[action.activity]
                        }
                        if (!Array.isArray(activity)) {
                            Logger.warn(`No access activity mapping available for feature activity '${action.activity}'`)
                            return a
                        }

                        a.push.apply(a, activity)
                        return a
                    }, [])
                    return accessStatement
                })
            })
    }

    asDocument() {
        return this._evaluate()
            .then(policy => {
                return policy.document
            })
    }

}

module.exports = {
    create(document) {
        return Q(new FeaturePolicy(document))
    },
    forPrincipals(principals, helpers) {
        return helpers.getPoliciesForPrincipals(principals)
            .then(documents => {
                const policy = new FeaturePolicy()
                return documents.reduce((acc, next) => {
                    return acc.mergeDocument(next)
                }, policy)
            })
    },
    getDbHelpers: DbHelpers.getHelpers,
    isValid
}



//////////////////
// WEBPACK FOOTER
// ./~/@gsm/authorizer/lib/feature-policy/index.js
// module id = 162
// module chunks = 51